import { X } from "lucide-react";
import { Reveal, Tag, Heading } from "./Reveal";
import { CTA } from "./CTA";
import { scrollToTarget } from "../lib/scroll";

const PILLARS = [
  { num: "01", title: "Good sourcing", text: "We've built relationships that give us access to quality cabinetry at unusually competitive pricing." },
  { num: "02", title: "Fair pricing", text: "We could simply add a giant markup. We'd rather build a giant list of happy customers." },
  { num: "03", title: "No nonsense", text: "We plan carefully, show up prepared, respect your home, and get the job done." },
];

const NEVER = [
  "No kitchen torn apart for three months.",
  "No wondering when the contractor is coming back.",
  "No surprise number at the end. $5K means $5K.",
  "No living indefinitely out of a microwave and takeout containers.",
];

export const HowItHappens = () => (
  <section className="relative py-24 lg:py-40 bg-sand overflow-hidden" data-testid="how-it-happens-section">
    <span
      className="pointer-events-none select-none absolute -right-6 -top-10 font-display font-bold text-outline text-[clamp(10rem,32vw,34rem)] leading-none tracking-[-0.06em]"
      aria-hidden="true"
    >
      $5K
    </span>
    <div className="relative mx-auto max-w-[1600px] px-5 sm:px-8">
      <Reveal className="max-w-4xl">
        <Tag>How does a $5K kitchen happen?</Tag>
        <Heading className="mt-6">Good sourcing. Fair pricing. No nonsense.</Heading>
      </Reveal>

      <div className="mt-20 grid md:grid-cols-3 border-t border-ink/15">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.1} className="border-b md:border-b-0 md:border-r border-ink/15 last:border-r-0 last:border-b-0">
            <div className="group h-full pt-8 pb-10 md:pr-8 md:pl-0 md:[&:not(:first-child)]:pl-8 transition-colors duration-500" data-testid={`pillar-${p.num}`}>
              <span className="font-display font-semibold text-6xl sm:text-7xl tracking-[-0.03em] text-ink/15 group-hover:text-teal transition-colors duration-500 leading-none">{p.num}</span>
              <h3 className="mt-8 font-display font-medium text-2xl sm:text-3xl tracking-[-0.015em] text-ink">{p.title}</h3>
              <p className="mt-4 text-base text-ink/65 leading-relaxed max-w-sm">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-20 grid lg:grid-cols-12 gap-10 items-start">
        <Reveal className="lg:col-span-6">
          <p className="text-lg text-ink/70 leading-relaxed max-w-xl">
            We're passing the savings along and making beautiful kitchen upgrades accessible to homeowners who assumed remodeling was going to cost far more than they wanted to spend. Up to 18 cabinets plus granite countertops, <span className="text-ink font-semibold">$5K guaranteed</span> — and most standard projects are completed in just a few days.
          </p>
          <CTA onClick={() => scrollToTarget("#quote-form")} testId="how-cost-btn" className="mt-10 min-w-[300px]">
            Show me what my kitchen could cost
          </CTA>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.1}>
          <ul className="divide-y divide-ink/15 border-y border-ink/15">
            {NEVER.map((n) => (
              <li key={n} className="flex items-start gap-4 py-5 font-display font-medium text-lg sm:text-xl tracking-[-0.005em] text-ink">
                <X className="h-5 w-5 mt-1 text-clay shrink-0" strokeWidth={2.5} />
                {n}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);
